/**
 * Console sink for the API clients' transport observability.
 *
 * The API clients log every failed response on purpose: Playwright and the
 * zero-console-error policy read these lines to diagnose a failure, so the
 * level is part of the contract. A typed refusal -- a 4xx whose body names a
 * machine-readable code the page renders as owner copy -- is an expected
 * outcome and is logged at info; anything else stays an error.
 */
const sink: Pick<Console, 'error' | 'info'> = globalThis.console;

export function logTransportError(label: string, payload: unknown): void {
  sink.error(label, payload);
}

const GENERIC_HTTP_CODE = /^HTTP_\d{3}$/;
// A refusal code is a machine token; an `error` field holding a human sentence
// is not one, and stays an error.
const MACHINE_TOKEN = /^[A-Za-z0-9_.:-]+$/;

function envelopeCode(value: unknown): string | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  for (const key of ['error_code', 'error', 'code'] as const) {
    const code = record[key];
    if (typeof code === 'string' && MACHINE_TOKEN.test(code) && !GENERIC_HTTP_CODE.test(code)) {
      return code;
    }
  }
  return null;
}

/**
 * The machine-readable refusal code of a 4xx response body, read at the top
 * level and inside FastAPI's `{detail: {...}}` wrapper. Null when the body
 * names no code (a bare `{detail: "..."}` or the gateway's generic
 * `HTTP_<status>` placeholder), so an unexplained 4xx still logs as an error.
 */
export function typedRefusalCode(status: number | undefined, data: unknown): string | null {
  if (status === undefined || status < 400 || status >= 500) return null;
  const topLevel = envelopeCode(data);
  if (topLevel !== null) return topLevel;
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return null;
  return envelopeCode((data as Record<string, unknown>).detail);
}

// Response bodies logged as an object serialize to "Object" under Playwright's
// console capture, which hides the error_code needed to diagnose failures.
// Stringify to a bounded JSON string so the body (and its error_code) is
// visible in captured console output.
function safeStringify(value: unknown, maxLength = 2000): string {
  if (value == null) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value.length > maxLength ? `${value.slice(0, maxLength)}…[truncated]` : value;
  }
  try {
    const serialized = JSON.stringify(value);
    if (serialized === undefined) {
      return String(value);
    }
    return serialized.length > maxLength
      ? `${serialized.slice(0, maxLength)}…[truncated]`
      : serialized;
  } catch {
    return '[unserializable]';
  }
}

/**
 * Log one failed API response. The body is stringified so its error code stays
 * readable in captured console output; a typed refusal logs at info with its
 * code, anything else as an error.
 */
export function logResponseFailure(
  label: string,
  failure: { status: unknown; method?: string; url?: string; data: unknown; headers: unknown },
): void {
  const payload = { ...failure, data: safeStringify(failure.data) };
  const status = typeof failure.status === 'number' ? failure.status : undefined;
  const refusalCode = typedRefusalCode(status, failure.data);
  if (refusalCode !== null) {
    sink.info(label, { ...payload, refusalCode });
    return;
  }
  sink.error(label, payload);
}
